import React from "react";
import { TestQuestionModel } from "p6m-p6u";
import { SummaryModes } from "../../../views/create/summary/Summary";
import { SummaryOpenModals } from "../../../views/create/summary/SummaryModals";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { Exercises, ExerciseTypes } from "../../../enums/exercises";
import ExerciseItemHeader from "./exerciseItemHeader/ExerciseItemHeader";
import ExerciseInfo from "./exerciseInfo/ExerciseInfo";
import ExerciseDetails from "./exerciseDetails/ExerciseDetails";
import SelectExerciseVocabulary from "./selectExerciseVocabulary/SelectExerciseVocabulary";
import ExerciseScore from "../../../basic/exerciseScore/ExerciseScore";
import useBreakpoints from "../../../hooks/useBreakpoints";

import { ExerciseContentContainer } from "./StyledComponents";

interface ExerciseItemProps {
    index: number;
    exerciseData?: TestQuestionModel;
    isExerciseOpen: boolean;
    isCreateNewExercise?: boolean;
    hasWords?: boolean;
    setCurrentMode: React.Dispatch<React.SetStateAction<SummaryModes>>;
    setOpenModal: React.Dispatch<React.SetStateAction<SummaryOpenModals | undefined>>;
    saveTestDataDebounced: (newTestContent: TestQuestionModel[]) => void;
    exerciseVocabularyOpenId: string;
    setExerciseVocabularyOpenId: React.Dispatch<React.SetStateAction<string>>;
}

const ExerciseItem: React.FC<ExerciseItemProps> = (props) => {
    const { currentExerciseModel, setCurrentExerciseModel, showScores } = useTestCreationContext();

    const { isAboveMD } = useBreakpoints();

    const isNewExercise = props.isCreateNewExercise && props.isExerciseOpen;

    const isMobileCompactCard = !isAboveMD && !props.isExerciseOpen;

    const isSelectExerciseVocabularyOpen = props.exerciseVocabularyOpenId === props.exerciseData?.exerciseId;

    const renderHeader = () => (
        <ExerciseItemHeader
            isNewExercise={isNewExercise}
            isEditMode={props.isExerciseOpen}
            exerciseData={props.exerciseData!}
            index={props.index}
            setCurrentMode={props.setCurrentMode}
            setOpenModal={props.setOpenModal}
            saveTestDataDebounced={props.saveTestDataDebounced}
            onAction={() => {
                props.setExerciseVocabularyOpenId("");
                setCurrentExerciseModel({});
            }}
        />
    );

    const renderInfo = () => <ExerciseInfo hasWords={props.hasWords} />;

    const renderDetails = () => {
        const showScore =
            showScores &&
            (props.hasWords || currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.FREE_TEXT));

        return (
            <ExerciseContentContainer>
                {showScore && <ExerciseScore />}

                <ExerciseDetails
                    hasWords={props.hasWords}
                    isSelectExerciseVocabularyOpen={isSelectExerciseVocabularyOpen}
                    onClickAddVocabularyButton={() =>
                        props.setExerciseVocabularyOpenId(props.exerciseData?.exerciseId || "")
                    }
                />
            </ExerciseContentContainer>
        );
    };

    const renderVocabulary = () => <SelectExerciseVocabulary onClose={() => props.setExerciseVocabularyOpenId("")} />;

    return (
        <div
            className={`flex-1 overflow-hidden rounded-small-container ${
                isMobileCompactCard ? "bg-white shadow-[0_0_1px_rgba(0,0,0,0.5)]" : "border border-p6-grey-lightest"
            }`}
        >
            {props.exerciseData && props.exerciseData.exerciseId && renderHeader()}
            {props.isExerciseOpen && (
                <>
                    {renderInfo()}
                    {renderDetails()}
                    {isSelectExerciseVocabularyOpen && renderVocabulary()}
                </>
            )}
        </div>
    );
};

export default ExerciseItem;
